import React from 'react';

import { LockFilled, UnlockFilled } from '@ant-design/icons';
import { Space } from 'antd';
import { isMobileOnly } from 'react-device-detect';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import UserPicture from '@/components/Common/UserPicture';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumTranslationKey } from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createTableActionsColumn,
  formatTZLocal,
  getEnumNamespace,
  getFlagEmoji,
} from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { registerModuleFilters } from '@/utils/input/filters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

const USER_DEVICE_ROW_ACTION_ORDER = {
  primary: [],
  secondary: ['detail', 'userDeviceLockToggle', 'delete'],
};

const LockedStatusFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'locked',
  label: 'userDevices.statusColumn',
  badgeOptions: {
    false: { status: 'success' },
    true: { status: 'error' },
  },
  badgeType: 'group-item',
  options: [
    { label: 'userDevices.status.enabled', value: 'false', locale: true },
    { label: 'userDevices.status.disabled', value: 'true', locale: true },
  ],
};

const UserDeviceUserFilter = {
  ...Filters.UserFilter,
  name: 'userId',
  label: 'userDevices.user',
};

const LastLoginDateFilter = {
  ...Filters.DateFilter,
  name: 'lastLogin',
  label: 'userDevices.lastLogin',
};

const AgentPlatformFilter = {
  ...BaseFilters.EnumFilter,
  name: 'agentPlatformType',
  label: 'userDevices.platform',
  enumNamespace: getEnumNamespace(CoreEnums.HttpUserAgentPlatformType),
  enumSource: CoreEnums.HttpUserAgentPlatformType,
  options: createEnumOptions(CoreEnums.HttpUserAgentPlatformType, 'userDevices.platformType', {
    casing: 'camel',
  }),
};

export function parseCountryIso2(rawCode) {
  if (rawCode == null || typeof rawCode !== 'string') return null;
  const trimmed = rawCode.trim();
  return /^[a-z]{2}$/i.test(trimmed) ? trimmed : null;
}

function mapCountryFilterOptions(data) {
  return data.map((item) => {
    const name = item.name || item.label;
    const iso2 = parseCountryIso2(item.code);
    if (!iso2 || !name) return item;

    return {
      ...item,
      name,
      label: `${getFlagEmoji(iso2)} ${name}`,
    };
  });
}

function buildCountrySearchFilter(searchTerm) {
  const sanitized = searchTerm.replace(/'/g, "''");
  return `contains(name,'${sanitized}')`;
}

const CountryFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'countryId',
  label: 'userDevices.country',
  labelInValue: true,
  displayField: 'name',
  apiRoute: 'country/paged',
  dataCacheKey: 'countryId',
  oDataField: 'countryId',
  filterSearch: true,
  buildRemoteSearchFilter: buildCountrySearchFilter,
  nomenclatureMapRemoteData: mapCountryFilterOptions,
  params: {
    select: 'id,name,code',
    orderBy: 'name asc',
  },
};

export const filtersConfig = [
  LockedStatusFilter,
  UserDeviceUserFilter,
  LastLoginDateFilter,
  AgentPlatformFilter,
  CountryFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

const PLATFORM_VALUES = Object.freeze(Object.values(CoreEnums.HttpUserAgentPlatformType));

function normalizeAgentPlatformValue(value) {
  if (value === '' || value === undefined || value === null) {
    return CoreEnums.HttpUserAgentPlatformType.Unknown;
  }
  const num = Number(value);
  if (!Number.isFinite(num) || !PLATFORM_VALUES.includes(num)) {
    return CoreEnums.HttpUserAgentPlatformType.Unknown;
  }
  return num;
}

export function formatAgentPlatformLabel(value, formatMessage) {
  const normalized = normalizeAgentPlatformValue(value);
  const key = getEnumTranslationKey(
    CoreEnums.HttpUserAgentPlatformType,
    normalized,
    'userDevices.platformType',
    false,
  );
  return formatMessage({ id: key });
}

export function wrapWithCountryFlag(iso2, labelNode) {
  if (!iso2) return labelNode;
  const upper = iso2.toUpperCase();
  return (
    <Space size={6} align="center">
      <span aria-hidden title={upper} style={{ fontSize: '1.2em', lineHeight: 1 }}>
        {getFlagEmoji(iso2)}
      </span>
      {labelNode}
    </Space>
  );
}

export function mapUserDeviceDetailFields(fields, item, formatMessage) {
  if (!item || !fields?.length) return fields;

  return fields.map((row) => {
    if (row.name === 'locked') {
      const isLocked = item.locked === true;
      return {
        ...row,
        type: 'badge',
        badgeType: 'group-item',
        badgeStatus: isLocked ? 'error' : 'success',
        value: isLocked ? 'userDevices.status.disabled' : 'userDevices.status.enabled',
        locale: true,
      };
    }
    if (row.name === 'agentPlatformType') {
      return {
        ...row,
        type: 'badge',
        badgeType: 'process',
        badgeStatus: 'default',
        value: formatAgentPlatformLabel(item.agentPlatformType, formatMessage),
        locale: false,
      };
    }
    if (row.name === 'country') {
      const country = item.country;
      const name = country?.name ?? row.value;
      if (name == null || name === '') {
        return { ...row, value: DEFAULT_EMPTY_VALUE };
      }
      const iso2 = parseCountryIso2(country?.code);
      return { ...row, value: wrapWithCountryFlag(iso2, name) };
    }
    return row;
  });
}

export function buildUserDeviceColumns({
  formatMessage,
  searchCriteria,
  openDrawerCreateOrEdit,
  deleteItemConfirm,
  toggleDeviceLocked,
  isMobileView = isMobileOnly,
}) {
  return [
    {
      key: 'locked',
      title: formatMessage({ id: 'userDevices.statusColumn' }),
      dataIndex: 'locked',
      width: 120,
      align: 'center',
      sorter: true,
      render: (value) => (
        <Badge
          text={formatMessage({
            id: value ? 'userDevices.status.disabled' : 'userDevices.status.enabled',
          })}
          status={value ? 'error' : 'success'}
          type="group-item"
        />
      ),
    },
    {
      key: 'user/fullName',
      title: formatMessage({ id: 'userDevices.user' }),
      dataIndex: ['user', 'fullName'],
      sorter: true,
      render: (_, record) => {
        const user = record?.user;
        const name = user?.fullName;
        if (!name) return DEFAULT_EMPTY_VALUE;

        const nameNode = createHighlightRender(searchCriteria)(name);
        const userId = user?.id ?? record?.userId;

        return (
          <Space size={8} align="center">
            {userId ? <UserPicture id={userId} name={name} size={28} /> : null}
            {nameNode}
          </Space>
        );
      },
    },
    {
      key: 'lastLogin',
      title: formatMessage({ id: 'userDevices.lastLogin' }),
      dataIndex: 'lastLogin',
      sorter: true,
      render: (v) => (v ? formatTZLocal(v) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'name',
      title: formatMessage({ id: 'userDevices.name' }),
      dataIndex: 'name',
      sorter: true,
      render: (value, record, index) => {
        const rendered = createHighlightRender(searchCriteria)(value, record, index);
        return rendered ?? DEFAULT_EMPTY_VALUE;
      },
    },
    {
      key: 'agentPlatformType',
      title: formatMessage({ id: 'userDevices.platform' }),
      dataIndex: 'agentPlatformType',
      align: 'center',
      sorter: true,
      render: (v) => {
        const textCell = formatAgentPlatformLabel(v, formatMessage);
        return <Badge status="default" type="process" text={textCell} />;
      },
    },
    {
      key: 'country/name',
      title: formatMessage({ id: 'userDevices.country' }),
      dataIndex: ['country', 'name'],
      sorter: true,
      render: (_, record) => {
        const country = record?.country;
        const name = country?.name;
        if (!name) return DEFAULT_EMPTY_VALUE;
        const nameNode = createHighlightRender(searchCriteria)(name);
        return wrapWithCountryFlag(parseCountryIso2(country?.code), nameNode);
      },
    },
    {
      key: 'city',
      title: formatMessage({ id: 'userDevices.region' }),
      dataIndex: 'city',
      sorter: true,
      render: (value, record, index) => {
        const rendered = createHighlightRender(searchCriteria)(value, record, index);
        return rendered ?? DEFAULT_EMPTY_VALUE;
      },
    },
    createTableActionsColumn({
      render: (_, record) => {
        const isLocked = Boolean(record?.locked);
        return (
          <TableRowButtons
            onDetail={() => openDrawerCreateOrEdit(record.id, true, record)}
            onRemove={() => deleteItemConfirm(record.id)}
            formatMessage={formatMessage}
            isMobileView={isMobileView}
            actionOrder={USER_DEVICE_ROW_ACTION_ORDER}
            customItems={[
              {
                key: 'userDeviceLockToggle',
                label: formatMessage({
                  id: isLocked ? 'userDevices.action.unlock' : 'userDevices.action.lock',
                }),
                icon: isLocked ? <UnlockFilled /> : <LockFilled />,
                onClick: (e) => {
                  if (e?.preventDefault) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  toggleDeviceLocked?.(record);
                },
              },
            ]}
          />
        );
      },
    }),
  ];
}
